import { FC, ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';
import classNames from 'classnames';

import styles from './styles.less';

function polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number) {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;

    return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians),
    };
}

function buildArc(x: number, y: number, radius: number, startAngle: number, endAngle: number) {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);

    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

    const d = ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y].join(' ');

    return d;
}

interface ProgressCircleProps {
    style: 'neutral' | 'positive';
    percent: number;
    size: 60 | 80;
    children?: ReactNode;
}

const ProgressCircle: FC<ProgressCircleProps> = ({ style, percent, size, children }) => {
    const [circleRef, circleInView] = useInView({ triggerOnce: true, threshold: 0.3 });

    const buildArgXY = size / 2;
    const radius = size === 80 ? 37 : 23;

    const classes = classNames(styles.circle, {
        [styles[`circle_${style}`]]: true,
    });

    return (
        <div ref={circleRef} className={classes} style={{ width: size, height: size }}>
            <span className={styles.circleInner}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={size}
                    height={size}
                    viewBox={`0 0 ${size} ${size}`}
                    fill="transparent"
                >
                    <path
                        className={styles.circleBase}
                        d={buildArc(buildArgXY, buildArgXY, radius, 0, 359)}
                        strokeWidth="6"
                        strokeLinecap="round"
                    />
                    {percent > 0 && circleInView && (
                        <path
                            className={styles.circleIndicator}
                            d={buildArc(buildArgXY, buildArgXY, radius, 0, (360 * percent) / 100 - 2)}
                            strokeWidth="6"
                            strokeLinecap="round"
                            pathLength={percent}
                            strokeDashoffset={-100}
                            strokeDasharray={100}
                        />
                    )}
                </svg>
            </span>
            {children}
        </div>
    );
};

export default ProgressCircle;
